import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PunchingTimePage.css';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaUsers, FaClock, FaSignInAlt, FaSignOutAlt, FaInfoCircle, FaUser } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const PunchingTimePage = () => {
  const { currentUser, userProfile } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    role: '',
    department: '',
    date: '',
    punchIn: '',
    punchOut: ''
  });
  const [punchingTimes, setPunchingTimes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUserPunchStatus, setCurrentUserPunchStatus] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    selectedUser: '',
    selectedRole: '',
    status: '', // 'punched-in', 'punched-out', 'incomplete'
    searchTerm: ''
  });
  const [filteredPunchingTimes, setFilteredPunchingTimes] = useState([]);
  
  // Use the centralized API configuration
  const API_URL = `${API_CONFIG.BASE_URL}/api`;

  // Local storage functions for punching time data
  const loadLocalPunchingTimes = () => {
    try {
      const stored = localStorage.getItem('punchingTimes');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load local punching times:', error);
      return [];
    }
  };

  const saveLocalPunchingTimes = (data) => {
    try {
      localStorage.setItem('punchingTimes', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save local punching times:', error);
    }
  };

  const loadLocalUsers = () => {
    try {
      const stored = localStorage.getItem('punchingTimeUsers');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load local users:', error);
      return [];
    }
  };

  const saveLocalUsers = (data) => {
    try {
      localStorage.setItem('punchingTimeUsers', JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save local users:', error);
    }
  };


  // Function to sync local punching times with current user data
  const syncLocalPunchingTimes = () => {
    try {
      const localTimes = loadLocalPunchingTimes();
      const currentEmail = currentUser?.email;
      const currentRole = localStorage.getItem('role');
      
      if (currentEmail && currentRole) {
        // Update any local records with current user info
        const updatedTimes = localTimes.map(record => {
          if (record.email === currentEmail && !record.name) {
            return {
              ...record,
              name: userProfile?.name || userProfile?.displayName || currentUser.displayName || 'Unknown User',
              role: currentRole
            };
          }
          return record;
        });
        
        if (JSON.stringify(updatedTimes) !== JSON.stringify(localTimes)) {
          saveLocalPunchingTimes(updatedTimes);
          setPunchingTimes(updatedTimes);
        }
      }
    } catch (error) {
      console.error('Failed to sync local punching times:', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      // Sync local data first, then fetch from server
      syncLocalPunchingTimes();
      fetchData();
      fetchAllUsers();
      fetchCurrentUserStatus();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply filters whenever punchingTimes or filters change
  useEffect(() => {
    let filtered = [...punchingTimes];
    
    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        const fromDate = new Date(filters.dateFrom);
        return recordDate >= fromDate;
      });
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        const toDate = new Date(filters.dateTo);
        return recordDate <= toDate;
      });
    }
    
    // User filter
    if (filters.selectedUser) {
      filtered = filtered.filter(record => record.email === filters.selectedUser);
    }
    
    // Role filter
    if (filters.selectedRole) {
      filtered = filtered.filter(record => record.role === filters.selectedRole);
    }
    
    // Status filter
    if (filters.status) {
      if (filters.status === 'punched-in') {
        filtered = filtered.filter(record => record.punchIn && !record.punchOut);
      } else if (filters.status === 'punched-out') {
        filtered = filtered.filter(record => record.punchIn && record.punchOut);
      } else if (filters.status === 'incomplete') {
        filtered = filtered.filter(record => !record.punchIn || !record.punchOut);
      }
    }
    
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(record => 
        record.name?.toLowerCase().includes(searchLower) ||
        record.email?.toLowerCase().includes(searchLower) ||
        record.role?.toLowerCase().includes(searchLower) ||
        record.department?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredPunchingTimes(filtered);
  }, [punchingTimes, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      try {
        const timesRes = await axios.get(`${API_URL}/punching-times`, { headers });
        setPunchingTimes(timesRes.data);
        setError('');
      } catch (timesErr) {
        console.error('Failed to fetch punching times:', timesErr);
        if (timesErr.response?.status === 404 || timesErr.response?.status === 400 || timesErr.message?.includes('404') || timesErr.message?.includes('400') || timesErr.message?.includes('Not Found')) {
          console.log('🔇 Punching times endpoint not available (404/400) - loading from local storage');
          const localData = loadLocalPunchingTimes();
          setPunchingTimes(localData);
          setError('Punching times endpoint not available. Using local data. Records will sync when server is available.');
        } else {
          setError(`Failed to fetch punching times: ${timesErr.response?.data?.error || timesErr.message}`);
        }
      }
      
      try {
        const statusRes = await axios.get(`${API_URL}/punching-times/current`, { headers });
        setCurrentUserPunchStatus(statusRes.data ? 'punched-in' : 'punched-out');
      } catch (statusErr) {
        console.log('🔇 Current status endpoint not available (404/400)');
        setCurrentUserPunchStatus('punched-out');
      }
    } catch (err) {
      console.error('Unexpected error in fetchData:', err);
      setError(`Unexpected error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserStatus = async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const statusRes = await axios.get(`${API_URL}/punching-times/current`, { headers });
      setCurrentUserPunchStatus(statusRes.data ? 'punched-in' : 'punched-out');
    } catch (err) {
      setCurrentUserPunchStatus('punched-out');
    }
  };


  const fetchAllUsers = async () => {
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const response = await axios.get(`${API_URL}/auth/users/all`, { headers });
      setAllUsers(response.data);
      saveLocalUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 404 || err.response?.status === 400 || err.message?.includes('404') || err.message?.includes('400') || err.message?.includes('Not Found')) {
        console.log('🔇 Users endpoint not available (404/400) - loading from local storage');
        const localUsers = loadLocalUsers();
        if (localUsers.length > 0) {
          setAllUsers(localUsers);
        } else {
          setAllUsers([]);
        }
      } else {
        setError('Failed to fetch users list');
      }
    }
  };

  const handleInput = (field, value) => {
    console.log(`Form input - ${field}:`, value);
    console.log('Current formData before update:', formData);
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      console.log('Updated formData:', updated);
      return updated;
    });
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      selectedUser: '',
      selectedRole: '',
      status: '',
      searchTerm: ''
    });
  };

  // Manual punch out functionality
  const handleManualPunchOut = async (recordId) => {
    if (!window.confirm('Are you sure you want to record a punch out for this record?')) {
      return;
    }

    try {
      setLoading(true);
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };

      await axios.post(
        `${API_URL}/auth/punching-times/record-logout`,
        { email: userProfile?.email },
        { headers }
      );

      // Refresh the data after successful punch out
      await fetchData();
      setError('');
      alert('Punch out recorded successfully!');
    } catch (err) {
      console.error('Manual punch out error:', err);
      setError(err.response?.data?.error || 'Failed to record punch out');
      alert('Failed to record punch out: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      
      console.log('Form data validation:', {
        email: formData.email,
        punchIn: formData.punchIn,
        emailValid: !!formData.email,
        punchInValid: !!formData.punchIn
      });
      console.log('Full formData object:', formData);
      
      // Validate required fields
      if (!formData.email || !formData.punchIn) {
        setError('Email and Punch In Time are required.');
        return;
      }
      
      console.log('Creating punching time with data:', formData);
      
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const response = await axios.post(
        `${API_URL}/punching-times`,
        formData,
        { headers }
      );
      console.log('Server response:', response.data);
      setPunchingTimes(prev => [...prev, response.data]);
      setFormData({ email: '', role: '', department: '', date: '', punchIn: '', punchOut: '' });
      setError('');
    } catch (err) {
      console.error('Failed to create punching time:', err);
      if (err.response?.status === 404 || err.response?.status === 400 || err.message?.includes('404') || err.message?.includes('400') || err.message?.includes('Not Found')) {
        console.log('🔇 404/400 detected - proceeding with local storage fallback');
        // Create local record with proper date/time conversion
        const punchInDateTime = new Date(`${formData.date}T${formData.punchIn}`);
        const punchOutDateTime = formData.punchOut ? new Date(`${formData.date}T${formData.punchOut}`) : null;
        
        const newRecord = {
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          email: formData.email,
          role: formData.role || 'staff',
          department: formData.department || null,
          name: allUsers.find(u => u.email === formData.email)?.name || 'Unknown',
          date: formData.date,
          punchIn: punchInDateTime.toISOString(),
          punchOut: punchOutDateTime ? punchOutDateTime.toISOString() : null,
          duration: punchOutDateTime ? Math.floor((punchOutDateTime - punchInDateTime) / (1000 * 60)) : null,
          createdAt: new Date().toISOString(),
          isLocal: true
        };
        
        console.log('Created local record:', newRecord);
        console.log('Punch In DateTime:', punchInDateTime);
        console.log('Punch In ISO:', punchInDateTime.toISOString());
        
        const updatedTimes = [...punchingTimes, newRecord];
        setPunchingTimes(updatedTimes);
        saveLocalPunchingTimes(updatedTimes);
        setFormData({ email: '', role: '', department: '', date: '', punchIn: '', punchOut: '' });
        setError('Punching time record added locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 3000);
      } else {
        setError(err.response?.data?.error || 'Failed to create punching time');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      setLoading(true);
      
      // Find the record to check if it's local
      const record = punchingTimes.find(item => item.id === id);
      
      if (record && record.isLocal) {
        // Handle local record update
        console.log('Updating local record:', id);
        const updatedRecord = {
          ...record,
          ...formData,
          name: allUsers.find(u => u.email === formData.email)?.name || record.name || 'Unknown',
          updatedAt: new Date().toISOString()
        };
        
        const updatedTimes = punchingTimes.map(item => 
          item.id === id ? updatedRecord : item
        );
        setPunchingTimes(updatedTimes);
        saveLocalPunchingTimes(updatedTimes);
        setEditingId(null);
        setFormData({ email: '', role: '', department: '', date: '', punchIn: '', punchOut: '' });
        setError('');
        return;
      }
      
      // Handle server record update
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const response = await axios.put(
        `${API_URL}/punching-times/${id}`,
        formData,
        { headers }
      );
      setPunchingTimes(prev =>
        prev.map(item => (item.id === id ? response.data : item))
      );
      setEditingId(null);
      setFormData({ email: '', role: '', department: '', date: '', punchIn: '', punchOut: '' });
      setError('');
    } catch (err) {
      console.error('Update error:', err);
      if (err.response?.status === 404 || err.response?.status === 400 || err.message?.includes('404') || err.message?.includes('400') || err.message?.includes('Not Found')) {
        console.log('🔇 404/400 detected - proceeding with local storage fallback for update');
        // Update local record as fallback
        const record = punchingTimes.find(item => item.id === id);
        if (record) {
          const updatedRecord = {
            ...record,
            ...formData,
            name: allUsers.find(u => u.email === formData.email)?.name || record.name || 'Unknown',
            updatedAt: new Date().toISOString(),
            isLocal: true
          };
          
          const updatedTimes = punchingTimes.map(item => 
            item.id === id ? updatedRecord : item
          );
          setPunchingTimes(updatedTimes);
          saveLocalPunchingTimes(updatedTimes);
          setEditingId(null);
          setFormData({ email: '', role: '', department: '', date: '', punchIn: '', punchOut: '' });
          setError('Record updated locally (API endpoint unavailable). Data will sync when server is available.');
          setTimeout(() => setError(''), 3000);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to update punching time');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this punching time record?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the record to check if it's local
      const record = punchingTimes.find(item => item.id === id);
      
      if (record && record.isLocal) {
        // Handle local record deletion
        console.log('Deleting local record:', id);
        const updatedTimes = punchingTimes.filter(item => item.id !== id);
        setPunchingTimes(updatedTimes);
        saveLocalPunchingTimes(updatedTimes);
        setError('');
        return;
      }
      
      // Handle server record deletion
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      await axios.delete(`${API_URL}/punching-times/${id}`, { headers });
      setPunchingTimes(prev => prev.filter(item => item.id !== id));
      setError('');
    } catch (err) {
      console.error('Delete error:', err);
      if (err.response?.status === 404 || err.response?.status === 400 || err.message?.includes('404') || err.message?.includes('400') || err.message?.includes('Not Found')) {
        console.log('🔇 404/400 detected - proceeding with local storage fallback for delete');
        // Delete local record as fallback
        const updatedTimes = punchingTimes.filter(item => item.id !== id);
        setPunchingTimes(updatedTimes);
        saveLocalPunchingTimes(updatedTimes);
        setError('Record deleted locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 3000);
      } else {
        setError(err.response?.data?.error || 'Failed to delete punching time');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    
    // Safely parse dates to avoid RangeError
    const parseDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      } catch (error) {
        console.error('Error parsing date:', error);
        return '';
      }
    };
    
    const parseTime = (timeString) => {
      if (!timeString) return '';
      try {
        const date = new Date(timeString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[1].substring(0, 5);
      } catch (error) {
        console.error('Error parsing time:', error);
        return '';
      }
    };
    
    setFormData({ 
      ...item,
      date: parseDate(item.date),
      punchIn: parseTime(item.punchIn),
      punchOut: parseTime(item.punchOut)
    });
  };

  const resetForm = () => {
    setFormData({ email: '', role: '', department: '', date: '', punchIn: '', punchOut: '' });
    setEditingId(null);
    setSelectedUser(null);
  };

  const handlePunchIn = async () => {
    if (!selectedUser) {
      setError('Please select a user first');
      return;
    }
    
    try {
      setLoading(true);
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      await axios.post(
        `${API_URL}/punching-times/record-login`,
        {
          email: selectedUser.email,
          role: selectedUser.role,
          name: selectedUser.name
        },
        { headers }
      );
      setCurrentUserPunchStatus('punched-in');
      fetchData(); // Refresh the list
      setError('');
    } catch (err) {
      console.error('Failed to record punch-in:', err);
      if (err.response?.status === 404 || err.response?.status === 400 || err.message?.includes('404') || err.message?.includes('400') || err.message?.includes('Not Found')) {
        console.log('🔇 404/400 detected - proceeding with local storage fallback for punch-in');
        // Create local punch-in record
        const punchInRecord = {
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          email: selectedUser.email,
          name: selectedUser.name,
          role: selectedUser.role,
          date: new Date().toISOString().split('T')[0],
          punchIn: new Date().toISOString(),
          punchOut: null,
          createdAt: new Date().toISOString(),
          isLocal: true
        };
        
        const updatedTimes = [...punchingTimes, punchInRecord];
        setPunchingTimes(updatedTimes);
        saveLocalPunchingTimes(updatedTimes);
        setCurrentUserPunchStatus('punched-in');
        setError('Punch-in recorded locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 3000);
      } else {
        setError(err.response?.data?.error || 'Failed to record punch-in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePunchOut = async () => {
    if (!selectedUser) {
      setError('Please select a user first');
      return;
    }
    
    try {
      setLoading(true);
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      await axios.post(
        `${API_URL}/punching-times/record-logout`,
        {
          email: selectedUser.email
        },
        { headers }
      );
      setCurrentUserPunchStatus('punched-out');
      fetchData(); // Refresh the list
      setError('');
    } catch (err) {
      console.error('Failed to record punch-out:', err);
      if (err.response?.status === 404 || err.response?.status === 400 || err.message?.includes('404') || err.message?.includes('400') || err.message?.includes('Not Found')) {
        console.log('🔇 404/400 detected - proceeding with local storage fallback for punch-out');
        // Find the latest punch-in record for this user and update it with punch-out
        const today = new Date().toISOString().split('T')[0];
        const userRecords = punchingTimes.filter(record => 
          record.email === selectedUser.email && 
          record.date === today && 
          !record.punchOut
        );
        
        if (userRecords.length > 0) {
          // Update the latest punch-in record with punch-out time
          const latestRecord = userRecords[userRecords.length - 1];
          const updatedTimes = punchingTimes.map(record => 
            record.id === latestRecord.id 
              ? { ...record, punchOut: new Date().toISOString() }
              : record
          );
          setPunchingTimes(updatedTimes);
          saveLocalPunchingTimes(updatedTimes);
        } else {
          // Create a new punch-out only record
          const punchOutRecord = {
            id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            email: selectedUser.email,
            name: selectedUser.name,
            role: selectedUser.role,
            date: today,
            punchIn: null,
            punchOut: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            isLocal: true
          };
          
          const updatedTimes = [...punchingTimes, punchOutRecord];
          setPunchingTimes(updatedTimes);
          saveLocalPunchingTimes(updatedTimes);
        }
        
        setCurrentUserPunchStatus('punched-out');
        setError('Punch-out recorded locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 3000);
      } else {
        setError(err.response?.data?.error || 'Failed to record punch-out');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData(prev => ({
      ...prev,
      email: user.email,
      role: user.role
    }));
  };

  const getStatusColor = (status) => {
    return status === 'punched-in' ? '#28a745' : '#dc3545';
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Get recent automatic login/logout records
  const getRecentAutoRecords = () => {
    const today = new Date().toISOString().split('T')[0];
    return punchingTimes.filter(record => 
      record.date === today && 
      (record.loginMethod === 'web' || record.logoutMethod === 'web')
    ).sort((a, b) => new Date(b.createdAt || b.punchIn || b.punchOut) - new Date(a.createdAt || a.punchIn || a.punchOut));
  };

  if (!currentUser) {
    return <div className="punching-time-page">Loading...</div>;
  }

  // Print function
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get the table HTML
    const table = document.querySelector('.data-table');
    if (!table) {
      alert('No table found to print');
      return;
    }
    
    // Clone the table and remove action column
    const tableClone = table.cloneNode(true);
    
    // Remove the Actions column (last column)
    const rows = tableClone.querySelectorAll('tr');
    rows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      if (cells.length > 8) {
        cells[cells.length - 1].remove(); // Remove the last cell (Actions column)
      }
    });
    
    // Get current date
    const currentDate = new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    
    // Write the print content
    printWindow.document.write(`
      <html>
        <head>
          <title>Punching Time Records Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px; 
              font-size: 18px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
              font-size: 12px;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              text-align: center;
            }
            .print-timestamp {
              text-align: right;
              margin-top: 20px;
              font-size: 10px;
              color: #666;
            }
            .role-badge {
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
            }
            .role-ADMIN {
              background-color: #ffc107;
              color: #000;
            }
            .role-STAFF {
              background-color: #17a2b8;
              color: #fff;
            }
            .role-SUPER_ADMIN {
              background-color: #6f42c1;
              color: #fff;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Punching Time Records Report</h1>
          <p style="text-align: center; margin-bottom: 20px;">Generated on: ${new Date().toLocaleDateString()}</p>
          <p style="text-align: center; margin-bottom: 20px; color: #666;">Showing: ${filteredPunchingTimes.length} of ${punchingTimes.length} records</p>
          ${tableClone.outerHTML}
          <div class="print-timestamp">Printed on: ${currentDate} IST</div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Download as PDF function
  const handleDownload = () => {
    const table = document.querySelector('.data-table');
    if (!table) {
      alert('No table found to download');
      return;
    }

    // Clone the table and remove all interactive elements
    const tableClone = table.cloneNode(true);
    
    // Remove ALL interactive elements more comprehensively
    const interactiveSelectors = [
      'button', 'input', 'select', 'textarea',
      '[onclick]', '[onchange]', '[oninput]', '[onmousedown]', '[onmouseup]',
      '.btn', '.button', '.form-control', '.form-select', '.input-group',
      '.actions', '.header-actions', '.controls', '.interactive',
      '.clickable', '.dropdown', '.nav', '.navbar'
    ];
    
    interactiveSelectors.forEach(selector => {
      const elements = tableClone.querySelectorAll(selector);
      elements.forEach(element => element.remove());
    });
    
    // Remove any elements with interactive classes or attributes
    const allElements = tableClone.querySelectorAll('*');
    allElements.forEach(element => {
      // Remove elements with interactive classes
      const classList = element.className;
      if (typeof classList === 'string') {
        const interactiveClasses = ['btn', 'button', 'input', 'select', 'form', 'interactive', 'clickable', 'actions'];
        const hasInteractiveClass = interactiveClasses.some(cls => classList.includes(cls));
        if (hasInteractiveClass) {
          element.remove();
          return;
        }
      }
      
      // Remove elements with event handlers
      const hasEventHandlers = element.onclick || element.onchange || element.oninput || 
                              element.onmousedown || element.onmouseup || element.onkeydown;
      if (hasEventHandlers) {
        element.remove();
        return;
      }
      
      // Remove elements with interactive attributes
      const interactiveAttrs = ['onclick', 'onchange', 'oninput', 'onmousedown', 'onmouseup', 'onkeydown'];
      const hasInteractiveAttr = interactiveAttrs.some(attr => element.hasAttribute(attr));
      if (hasInteractiveAttr) {
        element.remove();
      }
    });
    
    // Remove action columns and headers
    const actionHeaders = tableClone.querySelectorAll('th.actions, td.actions, .action-column');
    actionHeaders.forEach(element => element.remove());

    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Punching Time Records Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px; 
              font-size: 18px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
              font-size: 12px;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              text-align: center;
            }
            .role-badge { 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-size: 10px;
              color: white;
            }
            .role-staff { background-color: #007bff; }
            .role-admin { background-color: #28a745; }
            .role-super_admin { background-color: #dc3545; }
            button,
            input,
            select,
            textarea,
            .actions,
            .header-actions,
            .controls,
            .interactive,
            .clickable,
            .btn,
            .button,
            .form-control,
            .form-select,
            .input-group,
            .dropdown,
            .nav,
            .navbar,
            .no-print,
            [onclick],
            [onchange],
            [oninput],
            [onmousedown],
            [onmouseup],
            [onkeydown] { 
              display: none !important; 
              visibility: hidden !important;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Punching Time Records Report</h1>
          ${tableClone.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div className="punching-time-page">
      <div className="page-header">
        <h2><FaClock /> Staff Punching Time Management</h2>
        <div className="no-print" style={{ display: 'flex', gap: '10px' }}>
          <button className="refresh-btn" onClick={fetchData} disabled={loading}>
            🔄 Refresh
          </button>
          <button 
            onClick={handlePrint}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🖨️ Print
          </button>
          <button 
            onClick={handleDownload}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📄 PDF
          </button>
        </div>
      </div>
      
      {/* Current User Status */}
      {currentUser && (
        <div className="current-user-status no-print">
          <div className="user-info">
            <FaUser className="user-icon" />
            <span className="user-name">
              {userProfile?.name || userProfile?.displayName || currentUser.displayName || currentUser.email}
            </span>
            <span className="user-role">
              ({userProfile?.role || 'user'})
            </span>
          </div>
          <div className="punch-status">
            Status: <strong style={{ color: getStatusColor(currentUserPunchStatus) }}>
              {currentUserPunchStatus === 'punched-in' ? '🟢 Punched In' : '🔴 Punched Out'}
            </strong>
          </div>
          <div className="auto-tracking-info">
            <span style={{ color: '#28a745', fontSize: '12px' }}>
              📱 Auto-tracking: Login/Logout times are automatically recorded
            </span>
          </div>
        </div>
      )}
  
      {error && <div className="error-message">{error}</div>}
      
      {/* Filter Section */}
      <div className="filter-section no-print" style={{ backgroundColor: '#e3f2fd', border: '3px solid #2196f3', margin: '10px 0' }}>
        <div className="filter-header">
          <h4>🔍 Filter Records - ADMIN FILTERS</h4>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="today-filter-btn" 
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setFilters({
                  ...filters,
                  dateFrom: today,
                  dateTo: today
                });
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              📅 Today
            </button>
            <button 
              className="my-records-filter-btn" 
              onClick={() => {
                setFilters({
                  ...filters,
                  selectedUser: currentUser?.email || ''
                });
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              👤 My Records
            </button>
            <button 
              className="today-my-records-filter-btn" 
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setFilters({
                  ...filters,
                  dateFrom: today,
                  dateTo: today,
                  selectedUser: currentUser?.email || ''
                });
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              📅👤 Today's My Records
            </button>
            <button 
              className="clear-filters-btn" 
              onClick={clearFilters}
              style={{
                padding: '6px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
        
        <div className="filter-grid">
          {/* Date Range Filters */}
          <div className="filter-group">
            <label>Date From:</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          
          <div className="filter-group">
            <label>Date To:</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          
          {/* User Filter */}
          <div className="filter-group">
            <label>User:</label>
            <select
              value={filters.selectedUser}
              onChange={(e) => handleFilterChange('selectedUser', e.target.value)}
              style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '150px' }}
            >
              <option value="">All Users</option>
              {allUsers.map(user => (
                <option key={user.id} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
          
          {/* Role Filter */}
          <div className="filter-group">
            <label>Role:</label>
            <select
              value={filters.selectedRole}
              onChange={(e) => handleFilterChange('selectedRole', e.target.value)}
              style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">All Roles</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          
          {/* Status Filter */}
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">All Status</option>
              <option value="punched-in">Punched In (No Punch Out)</option>
              <option value="punched-out">Punched Out (Complete)</option>
              <option value="incomplete">Incomplete Records</option>
            </select>
          </div>
          
          {/* Search Filter */}
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search by name, email, role, or department..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              style={{ 
                padding: '6px', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                minWidth: '200px'
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Recent Auto Login/Logout Activity */}
      {getRecentAutoRecords().length > 0 && (
        <div className="recent-activity">
          <h4>📱 Recent Auto Login/Logout Activity (Today)</h4>
          <div className="activity-list">
            {getRecentAutoRecords().slice(0, 5).map((record, index) => (
              <div key={record.id || index} className="activity-item">
                <span className="activity-user">{record.name || record.email}</span>
                <span className="activity-time">
                  {record.punchIn ? `🟢 Login: ${new Date(record.punchIn).toLocaleTimeString()}` : ''}
                  {record.punchOut ? `🔴 Logout: ${new Date(record.punchOut).toLocaleTimeString()}` : ''}
                </span>
                <span className="activity-method">
                  {record.loginMethod === 'web' ? 'Auto Login' : record.logoutMethod === 'web' ? 'Auto Logout' : 'Manual'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="punch-status-container no-print">
        <div className="user-selector">
          <label>Select User:</label>
          <select 
            value={selectedUser?.email ?? ''} 
            onChange={(e) => {
              const user = allUsers.find(u => u.email === e.target.value);
              handleUserSelect(user);
            }}
            disabled={loading}
          >
            <option value="">Choose a user...</option>
            {allUsers.map(user => (
              <option key={user.id} value={user.email}>
                {user.name} ({user.email}) - {user.role}
              </option>
            ))}
          </select>
        </div>
        
        <div className="punch-buttons no-print">
          <button 
            className={`punch-in-btn ${currentUserPunchStatus === 'punched-in' || !selectedUser ? 'disabled' : ''}`}
            onClick={handlePunchIn}
            disabled={currentUserPunchStatus === 'punched-in' || !selectedUser || loading}
          >
            <FaSignInAlt /> Manual Punch In
          </button>
          <button 
            className={`punch-out-btn ${currentUserPunchStatus === 'punched-out' || !selectedUser ? 'disabled' : ''}`}
            onClick={handlePunchOut}
            disabled={currentUserPunchStatus === 'punched-out' || !selectedUser || loading}
          >
            <FaSignOutAlt /> Manual Punch Out
          </button>
          {selectedUser && (
            <span className="status-indicator">
              Current Status: <strong style={{ color: getStatusColor(currentUserPunchStatus) }}>
                {currentUserPunchStatus === 'punched-in' ? 'Punched In' : 'Punched Out'}
              </strong>
            </span>
          )}
        </div>
      </div>

      <div className="crud-form no-print">
        <h4><FaInfoCircle /> Manual Record Entry (For Override/Correction)</h4>
        <input
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => handleInput('email', e.target.value)}
          required
          disabled={loading}
        />
        <select
          value={formData.role}
          onChange={(e) => handleInput('role', e.target.value)}
          required
          disabled={loading}
        >
          <option value="">Select Role</option>
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <select
          value={formData.department}
          onChange={(e) => handleInput('department', e.target.value)}
          disabled={loading}
        >
          <option value="">Select Department</option>
          <option value="hr">HR</option>
          <option value="finance">Finance</option>
          <option value="marketing">Marketing</option>
          <option value="sales">Sales</option>
          <option value="purchase">Purchase</option>
          <option value="accounts">Accounts</option>
          <option value="admin">Admin</option>
          <option value="it">IT</option>
          <option value="operations">Operations</option>
          <option value="customer_service">Customer Service</option>
        </select>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => handleInput('date', e.target.value)}
          required
          disabled={loading}
        />
        <input
          type="time"
          value={formData.punchIn}
          onChange={(e) => {
            console.log('Punch In input changed:', e.target.value);
            handleInput('punchIn', e.target.value);
          }}
          required
          disabled={loading}
          placeholder="HH:MM"
        />
        <input
          type="time"
          value={formData.punchOut}
          onChange={(e) => {
            console.log('Punch Out input changed:', e.target.value);
            handleInput('punchOut', e.target.value);
          }}
          disabled={loading}
          placeholder="HH:MM"
        />

        {editingId ? (
          <>
            <button className="save-btn" onClick={() => handleUpdate(editingId)} disabled={loading}>
              <FaSave /> Save
            </button>
            <button className="cancel-btn" onClick={resetForm} disabled={loading}>
              <FaTimes /> Cancel
            </button>
          </>
        ) : (
          <button className="add-btn" onClick={handleCreate} disabled={loading}>
            <FaPlus /> Add Manual Record
          </button>
        )}
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3><FaUsers /> Punching Time Records</h3>
          <span className="record-count">
            Showing: {filteredPunchingTimes.length} of {punchingTimes.length} records
          </span>
        </div>
        
        {loading ? (
          <div className="loading-message">Loading punching time records...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Date</th>
                <th>Punch In</th>
                <th>Punch Out</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPunchingTimes.length > 0 ? (
                filteredPunchingTimes.map((record) => (
                  <tr key={record.id} className={!record.punchOut ? 'missing-punch' : ''}>
                    <td>
                      {record.name || 'N/A'}
                      {record.isLocal && <span style={{color: '#28a745', marginLeft: '5px'}} title="Stored locally">📱</span>}
                    </td>
                    <td>{record.email}</td>
                    <td>
                      <span className={`role-badge role-${record.role}`}>
                        {record.role}
                      </span>
                    </td>
                    <td>{record.department || 'N/A'}</td>
                    <td>{record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}</td>
                    <td>{record.punchIn ? new Date(record.punchIn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}</td>
                    <td>{record.punchOut ? new Date(record.punchOut).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}</td>
                    <td>{formatDuration(record.duration)}</td>
                    <td>
                      {!record.punchOut && record.email === userProfile?.email && (
                        <button 
                          className="punch-out-btn action-btn" 
                          onClick={() => handleManualPunchOut(record.id)} 
                          disabled={loading}
                          title="Record Punch Out"
                        >
                          <FaSignOutAlt />
                        </button>
                      )}
                      <button className="edit-btn action-btn" onClick={() => handleEdit(record)} disabled={loading}>
                        <FaEdit />
                      </button>
                      <button className="delete-btn action-btn" onClick={() => handleDelete(record.id)} disabled={loading}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">No punching time data available.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Mobile Card Layout */}
      <div className="mobile-cards-container">
        {loading ? (
          <div className="mobile-loading">
            <div className="mobile-loading-spinner"></div>
          </div>
        ) : filteredPunchingTimes.length === 0 ? (
          <div className="mobile-empty-state">
            <div className="mobile-empty-state-icon">⏰</div>
            <div className="mobile-empty-state-text">No punching time data available</div>
            <div className="mobile-empty-state-subtext">Punch in/out records will appear here</div>
          </div>
        ) : (
          filteredPunchingTimes.map((record) => (
            <div key={record.id} className={`mobile-card ${!record.punchOut ? 'missing-punch' : ''}`}>
              <div className="card-header">
                <h3 className="card-title">
                  {record.name || 'N/A'}
                  {record.isLocal && (
                    <span style={{color: '#28a745', marginLeft: '5px'}} title="Stored locally">📱</span>
                  )}
                </h3>
                <span className={`card-status ${record.punchOut ? 'active' : 'pending'}`}>
                  {record.punchOut ? 'Completed' : 'In Progress'}
                </span>
              </div>
              
              <div className="card-content">
                <div className="card-row">
                  <span className="card-label">Email</span>
                  <span className="card-value email">{record.email}</span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Role</span>
                  <span className="card-value">
                    <span className={`card-role role-${record.role}`}>
                      {record.role}
                    </span>
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Department</span>
                  <span className="card-value">{record.department}</span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Date</span>
                  <span className="card-value">
                    <span className="card-date">
                      {record.date ? new Date(record.date).toLocaleDateString() : 'N/A'}
                    </span>
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Punch In</span>
                  <span className="card-value">
                    <span className="card-time">
                      {record.punchIn ? new Date(record.punchIn).toLocaleTimeString() : 'N/A'}
                    </span>
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Punch Out</span>
                  <span className="card-value">
                    <span className="card-time">
                      {record.punchOut ? new Date(record.punchOut).toLocaleTimeString() : 'Not punched out'}
                    </span>
                  </span>
                </div>
                
                {record.punchOut && (
                  <div className="card-row">
                    <span className="card-label">Duration</span>
                    <span className="card-value">
                      {(() => {
                        if (record.punchIn && record.punchOut) {
                          const punchIn = new Date(record.punchIn);
                          const punchOut = new Date(record.punchOut);
                          const diffMs = punchOut - punchIn;
                          const hours = Math.floor(diffMs / (1000 * 60 * 60));
                          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                          return `${hours}h ${minutes}m`;
                        }
                        return 'N/A';
                      })()}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="card-actions">
                <button 
                  onClick={() => handleEdit(record)} 
                  className="card-action-btn edit"
                >
                  <FaEdit /> Edit
                </button>
                <button 
                  onClick={() => handleDelete(record.id)} 
                  className="card-action-btn delete"
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PunchingTimePage;